import React from 'react'
import { useFeatureAccess } from '@/hooks/useFeatureAccess'
import { FEATURE_IDS } from '@/config/features'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import UpgradeScreen from '@/assets/utils/screens/UpgradeScreen'
import { Check } from 'lucide-react'
import {
    TertiaryButton,
    SecondaryButton,
} from '@/features/survey-builder/Tabs/CustomButton'
import { Separator } from '@radix-ui/react-separator'

interface FeatureAccessProps {
    featureId: (typeof FEATURE_IDS)[keyof typeof FEATURE_IDS]
    currentLimit?: number
    children: React.ReactNode
    fallback?: React.ReactNode
}

export const FeatureAccess: React.FC<FeatureAccessProps> = ({
    featureId,
    currentLimit,
    children,
    fallback,
}) => {
    const {
        isEnabled,
        limit,
        shouldShowUpgradePrompt,
        upgradeMessage,
        handleUpgrade,
    } = useFeatureAccess(featureId)

    // Check if we should show the upgrade prompt
    const shouldShowLimitPrompt = () => {
        // If there's no limit (null) or limit is -1, feature is unlimited
        if (limit === null || limit === -1) return false

        // If current limit is not set, don't show prompt
        if (currentLimit === undefined) return false

        // Show prompt if current usage has reached or exceeded the limit
        return currentLimit >= limit
    }

    if (!isEnabled || shouldShowLimitPrompt()) {
        if (shouldShowUpgradePrompt && upgradeMessage) {
            return (
                <Dialog>
                    <DialogTrigger>
                        {fallback || (
                            <Button variant="outline">Upgrade Required</Button>
                        )}
                    </DialogTrigger>
                    <DialogContent className="max-w-fit px-0 pb-4">
                        <DialogHeader className="px-6">
                            <DialogTitle>{upgradeMessage.title}</DialogTitle>
                            <DialogDescription>
                                {upgradeMessage.description}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="flex items-center justify-between gap-8 mt-[32px] px-6">
                            <div className="h-[200px]">
                                <UpgradeScreen />
                            </div>
                            <div className="flex flex-col gap-6">
                                <ul className="space-y-4 w-[338px]">
                                    <li className="flex items-start gap-[10px]">
                                        <span className="mt-1 h-[32px] min-w-[32px] bg-[#E8DAFF] rounded-full flex items-center justify-center">
                                            <Check
                                                className="text-[#B9A7FF]"
                                                size={16}
                                            />
                                        </span>
                                        <div className="flex flex-col gap-1">
                                            <div className="font-[550] text-[14px] whitespace-nowrap">
                                                Build surveys without any limits
                                            </div>
                                            <div className="text-[#616161] text-[12px] font-[450] ">
                                                Unlimited no. of questions and
                                                active surveys
                                            </div>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-[10px]">
                                        <span className="mt-1 h-[32px] min-w-[32px] bg-[#E8DAFF] rounded-full flex items-center justify-center">
                                            <Check
                                                className="text-[#B9A7FF]"
                                                size={16}
                                            />
                                        </span>
                                        <div className="flex flex-col gap-1">
                                            <div className="font-[550] text-[14px] whitespace-nowrap">
                                                Customize your target audience
                                            </div>
                                            <div className="text-[#616161] text-[12px] font-[450]">
                                                Choose who sees your survey
                                            </div>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-[10px]">
                                        <span className="mt-1 h-[32px] min-w-[32px] bg-[#E8DAFF] rounded-full flex items-center justify-center">
                                            <Check
                                                className="text-[#B9A7FF]"
                                                size={16}
                                            />
                                        </span>
                                        <div className="flex flex-col gap-1 w-full">
                                            <div className="font-[550] text-[14px] whitespace-nowrap">
                                                Analyze and export survey
                                                insights
                                            </div>
                                            <div className="text-[#616161] text-[12px] font-[450]">
                                                Use our advanced filters to get
                                                actionable insights for your
                                                business
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <Separator className="bg-[#E0E0E0] h-[1px] mt-4" />
                        <div className="flex justify-end gap-4 items-center px-6">
                            <DialogClose asChild>
                                <SecondaryButton text={'Maybe later'} />
                            </DialogClose>

                            <TertiaryButton
                                onClick={handleUpgrade}
                                text={upgradeMessage.upgradeButtonText}
                            />
                        </div>
                    </DialogContent>
                </Dialog>
            )
        }
        return fallback || null
    }

    return <div className="flex items-center">{children}</div>
}
